import { useTranslation } from 'react-i18next';

import { Badge, cn } from '@kognitos/lattice';

/** Lifecycle stage an automation surface is showing. */
export type TAutomationStage = 'blueprint' | 'draft' | 'published' | 'viewing';

const STAGE_LABEL_KEY: Record<TAutomationStage, string> = {
  blueprint: 'automations.automation.blueprint',
  draft: 'automations.automation.draft',
  published: 'automations.automation.live',
  viewing: 'automations.automation.viewing',
};

/** Colour per stage lives in CSS because `--brand-50` is not exposed as a
 *  Tailwind colour token (see `.automation-stage--*` in `index.css`). */
const STAGE_CLASS: Record<TAutomationStage, string> = {
  blueprint: 'automation-stage--blueprint',
  draft: 'automation-stage--draft',
  published: 'automation-stage--published',
  viewing: 'automation-stage--viewing',
};

interface IAutomationStageBadgeProps {
  stage: TAutomationStage;
  /** Renders the badge at 50% opacity, for a deactivated automation. */
  isDeactivated?: boolean;
  /** Overrides the stage's default label, e.g. "Published" for a collection. */
  label?: string;
  className?: string;
  'data-testid'?: string;
}

/**
 * The automation's stage, as a dot plus an uppercase label. Shared by the
 * automation list cards and the document title bar so both read the same.
 */
export function AutomationStageBadge({
  stage,
  isDeactivated = false,
  label,
  className,
  'data-testid': dataTestId,
}: IAutomationStageBadgeProps) {
  const { t } = useTranslation('automations');

  return (
    <Badge
      variant="secondary"
      className={cn(
        'gap-1.5 bg-transparent p-0 text-xs font-medium uppercase tracking-wider',
        STAGE_CLASS[stage],
        isDeactivated && 'opacity-50',
        className,
      )}
      data-testid={dataTestId}
    >
      <span
        aria-hidden
        className="automation-stage-dot size-1.5 shrink-0 rounded-full"
      />
      {label ?? t(STAGE_LABEL_KEY[stage])}
    </Badge>
  );
}
